import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button, Checkbox, Select, SelectItem, Notice, Card,
} from "@conuti-das/prince-ui";
import { Flex } from "@/ui-prince";
import { createRun } from "@/api/runs";
import { enqueueStage } from "@/api/jobs";
import { getActor } from "@/api/client";

const ALL_STAGES = [
  "scrape", "mirror", "parse",
  "generate_bpmn", "generate_dmn", "generate_ticket",
  "process_repo", "camunda_deploy",
] as const;

const DOCTYPES = ["", "ahb-docx", "ebd-pdf", "mig-docx", "codelisten-xlsx", "bnetza-pdf"];

// react-aria Select keys must be stable, non-empty strings — der „alle"-Eintrag
// hat fachlich den leeren String als Wert, bekommt aber einen Sentinel-Key.
const ALL_DOCTYPES_KEY = "__all__";
const doctypeToKey = (d: string) => (d === "" ? ALL_DOCTYPES_KEY : d);
const keyToDoctype = (k: string) => (k === ALL_DOCTYPES_KEY ? "" : k);

export function RunComposer({ knownReleases }: { knownReleases: string[] }) {
  const nav = useNavigate();
  const [release, setRelease] = useState(knownReleases[0] ?? "202610");
  const [stages, setStages] = useState<Set<string>>(new Set(ALL_STAGES));
  const [doctype, setDoctype] = useState("");
  const [reason, setReason] = useState("");
  const [autoStart, setAutoStart] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function toggle(s: string) {
    const n = new Set(stages);
    if (n.has(s)) n.delete(s); else n.add(s);
    setStages(n);
  }

  async function submit() {
    setErr(null); setBusy(true);
    try {
      const scope: any = { release };
      if (doctype) scope.doctype = doctype;
      const run = await createRun({
        release,
        stages: [...stages],
        scope,
        triggered_by: getActor(),
        description: reason || undefined,
      });
      if (autoStart) {
        for (const s of stages) {
          try { await enqueueStage(run.id, s, { scope }); }
          catch { /* ignore individual fails, show on detail page */ }
        }
      }
      nav(`/runs/${run.id}`);
    } catch (e: any) {
      setErr(e.message);
    } finally { setBusy(false); }
  }

  return (
    <Card padding="none">
      <div style={{ padding: "var(--s-4)", display: "flex", flexDirection: "column", gap: "var(--s-3)" }}>
        <h3 style={{ margin: 0 }}>Neuer Run</h3>
        {err && <Notice tone="negative">{err}</Notice>}

        <div>
          <Select
            label="Release (Formatversion)"
            selectedKey={release}
            onSelectionChange={(key) => { if (key != null) setRelease(String(key)); }}
          >
            {knownReleases.map(r => <SelectItem key={r} id={r}>{r}</SelectItem>)}
          </Select>
        </div>

        <div>
          <label className="prn-field-label">Stages</label>
          <Flex style={{ flexWrap: "wrap", gap: "var(--s-2)" }}>
            {ALL_STAGES.map(s => (
              <Checkbox key={s} isSelected={stages.has(s)} onChange={() => toggle(s)}>{s}</Checkbox>
            ))}
          </Flex>
        </div>

        <div>
          <Select
            label="Doctype (optional)"
            selectedKey={doctypeToKey(doctype)}
            onSelectionChange={(key) => { if (key != null) setDoctype(keyToDoctype(String(key))); }}
          >
            {DOCTYPES.map(d => <SelectItem key={doctypeToKey(d)} id={doctypeToKey(d)}>{d || "— alle —"}</SelectItem>)}
          </Select>
        </div>

        <div>
          <label className="prn-field-label">Trigger-Grund</label>
          <textarea
            className="prn-input"
            value={reason}
            rows={2}
            onChange={(e) => setReason(e.target.value)}
            placeholder="z.B. Hotfix für FV 202610"
          />
        </div>

        <Checkbox isSelected={autoStart} onChange={() => setAutoStart(!autoStart)}>Sofort starten</Checkbox>

        <Flex style={{ justifyContent: "flex-end", gap: "var(--s-2)" }}>
          <Button variant="tinted" onPress={() => nav("/activity")}>Abbrechen</Button>
          <Button variant="filled" isDisabled={busy || stages.size === 0} onPress={submit}>
            {busy ? "Anlegen..." : "Run anlegen"}
          </Button>
        </Flex>
      </div>
    </Card>
  );
}
